import { SkipValidationCode } from "../../types/appointment";
import type { SlotAssignmentFilter } from "../../types/employeeServices";
import { apiUrl } from "../../utils/url";
import { toSlotAssignmentFilter } from "../../utils/slotAssignmentFilter";
import {
  EmployeeSlotWithTime,
  NextAvailableSlot,
  TimeSlot,
} from "../agenda/types";
import { getErrorMessage } from "../errors";
import { HttpClient, HttpError } from "../HttpClient";
import {
  CreateEmployeeSlotRequest,
  GetAvailableEmployeeSlotsParams,
  GetEmployeeSlotsByCalendarParams,
  GetNextAvailableEmployeeSlotParams,
  IEmployeeSlotsService,
  UpdateEmployeeSlotRequest,
} from "./IEmployeeSlotsService";

/*
 * Applied here rather than by each caller, so no booking flow can disagree:
 * a search pinned to one employee drops the service and species narrowing.
 */
const withAssignmentFilter = <
  P extends SlotAssignmentFilter & { employeeId?: number },
>({
  serviceId,
  speciesId,
  ...params
}: P) => ({
  ...params,
  ...toSlotAssignmentFilter(serviceId, speciesId, params.employeeId),
});

export class EmployeeSlotsService implements IEmployeeSlotsService {
  private httpClient: HttpClient;

  private static readonly HTTP_STATUS_UNPROCESSABLE_ENTITY = 422;
  private static readonly HTTP_STATUS_NOT_FOUND = 404;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async getAvailableSlots(
    params: GetAvailableEmployeeSlotsParams,
  ): Promise<TimeSlot[]> {
    try {
      const response = await this.httpClient.post<TimeSlot[]>(
        "/employeeSlots/available-service-slots",
        withAssignmentFilter(params),
      );
      return response;
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível obter horários disponíveis. Escolha outro funcionário ou intervalo e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async getNextAvailableSlot(
    params: GetNextAvailableEmployeeSlotParams,
  ): Promise<NextAvailableSlot> {
    try {
      const response = await this.httpClient.post<NextAvailableSlot>(
        "/employeeSlots/next-available-service-slot",
        withAssignmentFilter(params),
      );
      return response;
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível encontrar o próximo horário disponível. Tente outro funcionário, serviço ou data.",
      );
      throw new Error(message);
    }
  }

  async getEmployeeSlotsByCalendar(
    params: GetEmployeeSlotsByCalendarParams,
    signal?: AbortSignal,
  ): Promise<EmployeeSlotWithTime[]> {
    try {
      const response = await this.httpClient.post<EmployeeSlotWithTime[]>(
        "/employeeSlots/calendar",
        params,
        signal ? { signal } : {},
      );
      return response;
    } catch (error) {
      // Re-throw abort errors as-is so they can be handled by the caller
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      const message = getErrorMessage(
        error,
        "Não foi possível carregar os horários do calendário. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async createSlot(
    data: CreateEmployeeSlotRequest,
    skipValidationCodes?: SkipValidationCode[],
  ): Promise<number> {
    const queryParams = new URLSearchParams();
    if (skipValidationCodes && skipValidationCodes.length > 0) {
      skipValidationCodes.forEach(code => {
        queryParams.append("skipValidationCodes", code);
      });
    }

    const endpoint =
      queryParams.toString() !== ""
        ? `/employeeSlots?${queryParams.toString()}`
        : `/employeeSlots`;

    try {
      const response = await this.httpClient.post<number>(
        endpoint,
        data,
        {},
        true,
      );

      if (!Number.isFinite(response)) {
        throw new Error(
          "Não foi possível criar o horário. Atualize o calendário e tente novamente.",
        );
      }

      return response;
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível criar o horário. Confirme se não existe sobreposição e se a data é válida.",
      );
      throw new Error(message);
    }
  }

  async getSlotById(id: number): Promise<EmployeeSlotWithTime> {
    try {
      return await this.httpClient.get<EmployeeSlotWithTime>(
        `/employeeSlots/${id}`,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar os horários do calendário. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async updateSlot(
    id: number,
    data: UpdateEmployeeSlotRequest,
    isSingleDayUpdate?: boolean,
    skipValidationCodes?: SkipValidationCode[],
  ): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append("isSingleDayUpdate", String(isSingleDayUpdate ?? false));
    if (skipValidationCodes && skipValidationCodes.length > 0) {
      skipValidationCodes.forEach(code => {
        queryParams.append("skipValidationCodes", code);
      });
    }

    const endpoint = `/employeeSlots/${id}?${queryParams.toString()}`;

    try {
      await this.httpClient.put<void>(endpoint, data);
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível atualizar o horário. O horário pode estar bloqueado ou ter agendamentos que impedem esta alteração.",
      );
      throw new Error(message);
    }
  }

  async deleteSlot(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/employeeSlots/${id}`);
    } catch (error) {
      // Check for specific status codes that don't have errorCodes
      if (error instanceof HttpError) {
        if (
          error.status ===
            EmployeeSlotsService.HTTP_STATUS_UNPROCESSABLE_ENTITY &&
          !error.responseData?.errorCode
        ) {
          throw new Error(
            "Este horário não pode ser eliminado porque já tem um agendamento atribuído.",
          );
        }

        if (
          error.status === EmployeeSlotsService.HTTP_STATUS_NOT_FOUND &&
          !error.responseData?.errorCode
        ) {
          throw new Error(
            "Horário não encontrado. Atualize o calendário e tente novamente.",
          );
        }
      }

      // Use errorCode handler (will use errorCode if available, otherwise default message)
      const message = getErrorMessage(
        error,
        "Não foi possível eliminar o horário. O horário pode estar bloqueado ou ter agendamentos associados.",
      );
      throw new Error(message);
    }
  }

  async skipDate(id: number, skipOn: string): Promise<void> {
    try {
      const response = await this.httpClient.patch<void>(
        `/employeeSlots/${id}/skip-date?skipOn=${encodeURIComponent(skipOn)}`,
        {},
      );
      return response;
    } catch (error) {
      // Check for specific status codes that don't have errorCodes
      if (error instanceof HttpError) {
        if (
          error.status === EmployeeSlotsService.HTTP_STATUS_NOT_FOUND &&
          !error.responseData?.errorCode
        ) {
          throw new Error(
            "Horário não encontrado. Atualize o calendário e tente novamente.",
          );
        }

        if (
          error.status ===
            EmployeeSlotsService.HTTP_STATUS_UNPROCESSABLE_ENTITY &&
          !error.responseData?.errorCode
        ) {
          throw new Error(
            "Este horário não pode ser cancelado porque já tem agendamentos atribuídos.",
          );
        }
      }

      // Use errorCode handler (will use errorCode if available, otherwise default message)
      const message = getErrorMessage(
        error,
        "Não foi possível cancelar este evento. O horário pode estar bloqueado ou ter agendamentos associados.",
      );
      throw new Error(message);
    }
  }
}
